import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { Observable } from 'rxjs';
import { PrefersReducedMotionService } from '../motion/prefers-reduced-motion.service';

/**
 * How the field's cards are arranged:
 *  - `scattered`: drifting at random places (sign-in, Connect);
 *  - `clustered`: gathered into three loose groups on the right, the rest faded (the chapters);
 *  - `scanning`: three tighter groups at the scan's moment rows, brighter (the scan screen).
 */
export type DarkEntryFieldLayout = 'scattered' | 'clustered' | 'scanning';

/** The fixed placement of one card (v9 `fieldBase`). */
export interface DarkEntryFieldSeed {
    readonly id: number;
    /** Random place (percent of the stage). */
    readonly rx: number;
    readonly ry: number;
    /** Random tilt (degrees) and scale. */
    readonly rot: number;
    readonly sc: number;
    /** Drift period and phase (seconds; the phase is negative so cards start mid-drift). */
    readonly dur: number;
    readonly del: number;
    /** Grid place (percent), reached at progress 1. */
    readonly gx: number;
    readonly gy: number;
    /** The group of a clustered layout (0-2), or -1 for a card that only fades. */
    readonly cl: number;
    /** The card's place inside its group. */
    readonly j: number;
    /** A background card that fades the most when clustered. */
    readonly noise: boolean;
}

/** One card as the template draws it (precomputed; the template holds no logic). */
export interface DarkEntryFieldCard {
    readonly id: number;
    readonly left: number;
    readonly top: number;
    readonly transform: string;
    readonly opacity: number;
    readonly driftSeconds: number;
    readonly driftDelaySeconds: number;
}

/** The number of cards in the field (v9). */
export const DARK_ENTRY_FIELD_CARD_COUNT = 42;

/** Group centres (percent) of the chapters' clustered layout and of the scan (v9). */
const CLUSTER_CENTRES: readonly (readonly [number, number])[] = [
    [84, 18],
    [88, 60],
    [78, 88],
];
const SCAN_CENTRES: readonly (readonly [number, number])[] = [
    [82, 16],
    [82, 50],
    [82, 82],
];

/**
 * The fixed placements of the field: the v9 seeded generator (seed 7, the same linear congruential sequence), so the
 * field looks the same on every load and on the sign-in page and the onboarding stage.
 */
export function seedDarkEntryField(count: number = DARK_ENTRY_FIELD_CARD_COUNT): DarkEntryFieldSeed[] {
    const seeds: DarkEntryFieldSeed[] = [];
    let seed = 7;
    const rnd = (): number => {
        seed = (seed * 9301 + 49297) % 233280;
        return seed / 233280;
    };

    for (let k = 0; k < count; k++) {
        seeds.push({
            id: k,
            rx: 3 + rnd() * 94,
            ry: 4 + rnd() * 90,
            rot: (rnd() - 0.5) * 50,
            sc: 0.6 + rnd() * 0.7,
            dur: 9 + rnd() * 9,
            del: -rnd() * 10,
            gx: 5 + (k % 7) * 15,
            gy: 8 + Math.floor(k / 7) * 15.5,
            cl: k % 7 < 3 ? k % 7 : -1,
            j: Math.floor(k / 7),
            noise: k % 7 === 6,
        });
    }
    return seeds;
}

/**
 * The cards of a layout (v9 field maths, without the prototype's fixture words and scan-phase extras).
 *
 * @param seeds the fixed placements.
 * @param progress 0 (scattered, tilted) to 1 (on the grid, upright); clamped.
 * @param layout the arrangement.
 */
export function layoutDarkEntryField(
    seeds: readonly DarkEntryFieldSeed[],
    progress: number,
    layout: DarkEntryFieldLayout
): DarkEntryFieldCard[] {
    const prog: number = clamp(typeof progress === 'number' && isFinite(progress) ? progress : 0, 0, 1);
    const scanning: boolean = layout === 'scanning';
    const clustered: boolean = scanning || layout === 'clustered';
    const centres: readonly (readonly [number, number])[] = scanning ? SCAN_CENTRES : CLUSTER_CENTRES;

    return seeds.map((b: DarkEntryFieldSeed): DarkEntryFieldCard => {
        let x: number = b.rx + (b.gx - b.rx) * prog;
        let y: number = b.ry + (b.gy - b.ry) * prog;
        let rot: number = b.rot * (1 - prog);
        let sc: number = b.sc + (1 - b.sc) * prog * 0.7;
        let fade: number = 0.2 + prog * 0.12;
        let centre: readonly [number, number];
        let cx: number;
        let tight: number;
        let ang: number;

        if (clustered && b.cl > -1) {
            centre = centres[b.cl];
            cx = centre[0];
            tight = scanning ? 8 : 4;
            if (b.cl === 0 && !scanning) {
                cx = centre[0] + (b.j < 3 ? -5 : 5);
            }
            ang = (b.j / 6) * Math.PI * 2 + b.cl * 0.7;
            x = Math.min(97, cx + Math.cos(ang) * tight * 0.8);
            y = Math.max(3, Math.min(97, centre[1] + Math.sin(ang) * tight * 1.4));
            rot = 0;
            sc = 0.6;
            fade = scanning ? 0.85 : 0.4;
        } else if (clustered) {
            fade = b.noise ? 0.03 : scanning ? 0.08 : 0.05;
        }
        return {
            id: b.id,
            left: round(x),
            top: round(y),
            transform: 'translate(-50%, -50%) rotate(' + round(rot) + 'deg) scale(' + round(sc) + ')',
            opacity: round(fade),
            driftSeconds: round(b.dur),
            driftDelaySeconds: round(b.del),
        };
    });
}

/** The fixed placements, computed once. */
const FIELD_SEEDS: readonly DarkEntryFieldSeed[] = seedDarkEntryField();

/**
 * `vf-dark-entry-field` — the decorative background field of the dark stage (P0-10, ported from v9): soft cards that
 * drift, then gather into groups as Trovve learns. Standalone, so the dark sign-in (D-01, AccountModule) imports it
 * without the lazy onboarding module.
 *
 * It is decorative only: the host is `aria-hidden` and takes no pointer events (OB-A-04). Under reduced motion nothing
 * drifts and cards move to their new places at once (PrefersReducedMotionService, plus the CSS media query; OB-S-04,
 * OB-E-09). The host fills its nearest positioned ancestor, so place it inside the dark surface.
 *
 * Its stylesheet carries the Instrument Sans `@font-face` (`de.fonts`) and the dark tokens, so a page that shows the
 * field has the dark stage's type.
 */
@Component({
    selector: 'vf-dark-entry-field',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './dark-entry-field.component.html',
    styleUrls: ['./dark-entry-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        'aria-hidden': 'true',
    },
})
export class DarkEntryFieldComponent implements OnChanges {
    /** 0 (scattered, tilted) to 1 (on the grid). */
    @Input() progress = 0;

    /** The arrangement of the cards. */
    @Input() layout: DarkEntryFieldLayout = 'scattered';

    /** One light sweep across the field (the scan's start); never shown under reduced motion. */
    @Input() sweep = false;

    /** The cards to draw. */
    cards: readonly DarkEntryFieldCard[] = layoutDarkEntryField(FIELD_SEEDS, 0, 'scattered');

    /** Whether reduced motion is on (the still class). */
    readonly still$: Observable<boolean>;

    constructor(motion: PrefersReducedMotionService) {
        this.still$ = motion.reduced$;
    }

    ngOnChanges(): void {
        this.cards = layoutDarkEntryField(FIELD_SEEDS, this.progress, this.layout);
    }

    trackById(index: number, card: DarkEntryFieldCard): number {
        return card.id;
    }
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function round(value: number): number {
    return Math.round(value * 100) / 100;
}
